  import { ISharetribeRepository } from '../../domain/repositories/ISharetribeRepository';
  import { Sharetribe } from '../../domain/entities/Sharetribe';
  import { SharetribeResponse, CreateSharetribeRequest, UpdateSharetribeRequest , CreateSharetribeUserRequest , SharetribeUserResponse } from '../../types';
//  import { ApiService } from '../../services/api';
import { ApiService } from "../../services/ApiService";  
import { InternalServerError } from '../../application/errors/AppError';
  import { ConfigManager } from '../../application/config/Config';

  /**
   * Infrastructure Repository Implementation for Sharetribe operations
   * Follows Dependency Inversion Principle
   */
  export class SharetribeRepository implements ISharetribeRepository {
  constructor(private readonly apiService: ApiService) {}
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly baseUrl = this.config.shareTribeBaseUrl;
  private readonly shareTribe_client_id = this.config.shareTribe_client_id;
  private readonly shareTribe_client_secret = this.config.shareTribe_client_secret;
  productProcessAlias = "flex-product-default-process/release-1";
  serviceProcessAlias = "flex-default-process/release-1";

async transactionsQuery(
  only: string,
  lastTransitions: string | null = null,
  include: string[] | null = null
): Promise<any[] | null> {

  if (!lastTransitions) {
    lastTransitions =
      "transition/enquire,transition/request-payment,transition/request-payment-after-enquiry,transition/expire-payment,transition/confirm-payment,transition/decline,transition/expire,transition/accept,transition/cancel,transition/complete,transition/expire-review-period,transition/review-1-by-customer,transition/review-1-by-provider,transition/review-2-by-provider,transition/expire-provider-review-period,transition/review-2-by-customer,transition/expire-customer-review-period,transition/mark-delivered,transition/mark-received,transition/auto-mark-received,transition/mark-received-from-purchased,transition/mark-received-from-disputed,transition/dispute,transition/cancel,transition/auto-cancel,transition/cancel-from-disputed,transition/auto-cancel-from-disputed,transition/auto-complete";
  }

  const params = new URLSearchParams();

  params.append("lastTransitions", lastTransitions);

  if (only) params.append("only", only);

  // FIX: join array into CSV string
  if (include && include.length > 0) {
    params.append("include", include.join(","));
  }

  let url = `${this.baseUrl}/api/transactions/query?${params.toString()}`;

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "GET",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Accept": "application/json"
      }
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (newTok) resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    console.error("transactionsQuery ERR:", await resp.text());
    return null;
  }

  const json = await resp.json();

  return json?.data || [];
}

async stripeAccountFetch(): Promise<any> {
  const url = `${this.baseUrl}/api/stripe_account/fetch?expand=true`;

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "GET",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Accept": "application/json"
      }
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (newTok) resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    console.error("stripeAccountFetch ERR:", await resp.text());
    return null;
  }

  return resp.json();
}

async addPayoutDetails(bankAccountToken: string): Promise<any> {
  const url = `${this.baseUrl}/api/stripe_account/update`;

  const token = localStorage.getItem("flex_token");

  const payload = {
    data: {
      attributes: {
        bankAccountToken
      }
    }
  };

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "Accept": "application/json"
    },
    body: JSON.stringify(payload)
  });

  if (!resp.ok) {
    console.error("addPayoutDetails ERR:", await resp.text());
    return null;
  }

  return resp.json();
}

async stripeAccountLinksCreate(
  failureURL: string,
  successURL: string,
  type: string
): Promise<string | null> {

  const url = `${this.baseUrl}/api/stripe_account_links/create?expand=true`;

  const body = {
    failureURL,
    successURL,
    type,
    collect: "currently_due"
  };

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (newTok) resp = await makeRequest(newTok);
  }

  if (!resp.ok) return null;

  const json = await resp.json();
  return json?.data?.attributes?.url || null;
}

async stripeSetupIntentsCreate(): Promise<any> {
  const url = `${this.baseUrl}/api/stripe_setup_intents/create?expand=true`;

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({})
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (!newTok) return null;
    resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    // Stripe setup intent creation failed
    // In production, consider logging to error tracking service
    return null;
  }

  return resp.json();
}

async stripeSetupIntentsConfirm(
  stripeSetupIntentId: string,
  clientSecret: string,
  paymentMethodData: any
): Promise<string | null> {

  const url = `${this.baseUrl}/api/stripe_setup_intents/confirm?expand=true`;

  const params = new URLSearchParams();
  params.append("stripeSetupIntentId", stripeSetupIntentId);
  params.append("clientSecret", clientSecret);

  // EXACT fields used by iOS
  params.append("name", paymentMethodData.name);
  params.append("email", paymentMethodData.email);
  params.append("line1", paymentMethodData.line1);
  params.append("line2", paymentMethodData.line2 || "");
  params.append("city", paymentMethodData.city);
  params.append("state", paymentMethodData.state || "");
  params.append("country", paymentMethodData.country);
  params.append("postal_code", paymentMethodData.postal_code);

  params.append("card_number", paymentMethodData.card_number);
  params.append("card_cvc", paymentMethodData.card_cvc);
  params.append("card_exp_month", paymentMethodData.card_exp_month);
  params.append("card_exp_year", paymentMethodData.card_exp_year);

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "application/json"
      },
      body: params.toString()
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (!newTok) return null;
    resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    // Payment confirmation failed
    // In production, consider logging to error tracking service
    return null;
  }

  const result = await resp.json();

  return (
    result?.data?.attributes?.stripePaymentMethodId ||
    result?.attributes?.stripePaymentMethodId ||
    result?.stripePaymentMethodId ||
    null
  );
}

async stripeCustomerCreate(
  stripePaymentMethodId: string | null,
  stripeCustomerEmail: string | null
): Promise<any> {

  let qp = "expand=true&defaultPaymentMethod=true";
  const url = `${this.baseUrl}/api/stripe_customer/create?${qp}`;

  const body: any = {};
  if (stripePaymentMethodId) body.stripePaymentMethodId = stripePaymentMethodId;
  if (stripeCustomerEmail) body.stripeCustomerEmail = stripeCustomerEmail;

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (!newTok) return null;
    resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    // Stripe customer creation failed
    // In production, consider logging to error tracking service
    return null;
  }

  return resp.json();
}

async stripeCustomerAddPaymentMethod(stripePaymentMethodId: string): Promise<any> {
  const url = `${this.baseUrl}/api/stripe_customer/add_payment_method?expand=true`;

  const body = { stripePaymentMethodId };

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (!newTok) return null;
    resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    // Failed to add payment method to Stripe customer
    // In production, consider logging to error tracking service
    return null;
  }

  return resp.json();
}

async stripeCustomerDeletePaymentMethod(): Promise<any> {
  const url = `${this.baseUrl}/api/stripe_customer/delete_payment_method?expand=true`;

  let token = localStorage.getItem("flex_token");

  const makeRequest = async (tok: string | null) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Authorization": tok ? `Bearer ${tok}` : "",
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({})
    });

  let resp = await makeRequest(token);

  if (resp.status === 401) {
    const newTok = await this.retryWithRefreshToken();
    if (!newTok) return null;
    resp = await makeRequest(newTok);
  }

  if (!resp.ok) {
    // Failed to delete payment method from Stripe customer
    // In production, consider logging to error tracking service
    return null;
  }

  return resp.json();
}

async transactionsTransition(
  transactionId: string,
  transition: string,
  transitionParams: Record<string, any> | null
): Promise<any> {

  try {
    const url = `${this.baseUrl}/api/transactions/transition?id=${transactionId}&expand=true`;

    const body = {
      id: transactionId,
      transition,
      params: transitionParams || {}
    };

    let token = localStorage.getItem("flex_token");

    let resp = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify(body)
    });

    // Handle expired token
    if (resp.status === 401) {
      const newToken = await this.retryWithRefreshToken();
      if (!newToken) return null;

      resp = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${newToken}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      });
    }

    const text = await resp.text();
    return text ? JSON.parse(text) : null;

  } catch (err) {
    // Transaction transition failed
    // In production, consider logging to error tracking service
    return null;
  }
}


  // ----------------------------------------
  //  INITIATE TRANSACTION
  // ----------------------------------------
  async transactionsInitiate(
    transition: string,
    transitionParams: Record<string, any>,
    processAlias: string
  ): Promise<any> {

    const url = `${this.baseUrl}/api/transactions/initiate?expand=true`;

    const body = {
      processAlias,
      transition,
      params: transitionParams
    };

    let token = localStorage.getItem("flex_token");

    const makeRequest = async (tok: string | null) => {
      return fetch(url, {
        method: "POST",
        headers: {
          "Authorization": tok ? `Bearer ${tok}` : "",
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      });
    };

    let resp = await makeRequest(token);

    // Token expired
    if (resp.status === 401) {
      const newToken = await this.retryWithRefreshToken();
      if (!newToken) return null;
      resp = await makeRequest(newToken);
    }

    // Token needs exchange (service provider)
    if (resp.status === 403 || resp.status === 409) {
      const exchangedToken = await this.retryWithTokenExchange();
      if (!exchangedToken) return null;
      resp = await makeRequest(exchangedToken);
    }

    const text = await resp.text();
    return text ? JSON.parse(text) : null;
  }

async retryWithTokenExchange(): Promise<string | null> {
  try {
    const url = `${this.baseUrl}/auth/token`;

    const accessToken = localStorage.getItem("flex_token");
    if (!accessToken) {
      // No access token available for token exchange
      return null;
    }

    const CLIENT_ID = this.shareTribe_client_id;
    const CLIENT_SECRET = this.shareTribe_client_secret;

    const body = new URLSearchParams({
      grant_type: "token_exchange",
      subject_token: accessToken,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      scope: "trusted:user"
    });

    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body
    });

    if (!resp.ok) {
      // Token exchange failed
      // In production, consider logging to error tracking service
      return null;
    }

    const json = await resp.json();

    if (json.access_token) {
      localStorage.setItem("flex_token", json.access_token);
      return json.access_token;
    }

    return null;

  } catch (err) {
    // Token exchange retry failed
    // In production, consider logging to error tracking service
    return null;
  }
}

 async createUser(payload: CreateSharetribeUserRequest): Promise<SharetribeUserResponse> {
  try {
    const fullUrl = `${this.baseUrl}/api/current_user/create?expand=true`;


    const response = await this.apiService.post<SharetribeUserResponse>(
      fullUrl,   
      payload,
      true,
      'main'
    );

    if (!response) {
      throw new InternalServerError('Sharetribe: No response received');
    }

    return response;
  } catch (error) {
    // Sharetribe createUser failed
    // In production, consider logging to error tracking service
    throw error;
  }
}

// async login(username: string, password: string): Promise<any | null> {
//   try {
//     const url = `${this.baseUrl}/auth/token`;

//     const body = new URLSearchParams({
//       client_id: this.shareTribe_client_id,
//       grant_type: "password",
//       scope: "user",
//       username,
//       password
//     });

//     const response = await fetch(url, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/x-www-form-urlencoded"
//       },
//       body
//     });

//     if (!response.ok) {
//       return null;
//     }

//     const json = await response.json();

//     return json;

//   } catch (err) {
//     return null;
//   }
// }

async login(username: string, password: string): Promise<any | null> {
  try {
    const url = `${this.baseUrl}/auth/token`;

    const body = new URLSearchParams({
      client_id: this.shareTribe_client_id,   // REQUIRED (missing earlier)
      grant_type: "password",
      scope: "user",
      username,
      password
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (err) {
    return null;
  }
}


async currentUserCreate(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  displayName?: string | null,
  bio?: string | null,
  publicData?: Record<string, any> | null,
  protectedData?: Record<string, any> | null,
  privateData?: Record<string, any> | null
): Promise<any> {
  try {
    const url = `${this.baseUrl}/api/current_user/create?expand=true`;

    const payload: any = {
      email: email.toLowerCase(),
      password,
      firstName,
      lastName
    };

    if (displayName) payload.displayName = displayName;
    if (bio) payload.bio = bio;
    if (publicData) payload.publicData = publicData;
    if (protectedData) payload.protectedData = protectedData;
    if (privateData) payload.privateData = privateData;

    const response = await this.apiService.post<any>(
      url,
      payload,
      true,
      'main'
    );

    if (!response) {
      throw new InternalServerError("Sharetribe: No response received");
    }

    return response;
  } catch (err) {
    // Sharetribe currentUserCreate failed
    // In production, consider logging to error tracking service
    return null;
  }
}

async updateProfile(
  firstName?: string | null,
  lastName?: string | null,
  displayName?: string | null,
  bio?: string | null,
  publicData?: Record<string, any> | null,
  protectedData?: Record<string, any> | null,
  privateData?: Record<string, any> | null,
  profileImageId?: string | null
): Promise<any> {
  const url = `${this.baseUrl}/api/current_user/update_profile?expand=true`;

  const payload: any = {};
  if (firstName) payload.firstName = firstName;
  if (lastName) payload.lastName = lastName;
  if (displayName) payload.displayName = displayName;
  if (bio) payload.bio = bio;
  if (publicData) payload.publicData = publicData;
  if (protectedData) payload.protectedData = protectedData;
  if (privateData) payload.privateData = privateData;
  if (profileImageId) payload.profileImageId = profileImageId;

  return this.withAuthRetry(async () => {
    return await this.apiService.post<any>(url, payload, true, "main");
  });
}

async currentUserShow(
  includeProfileImage: boolean = false,
  includeStripeAccount: boolean = false,
  includeStripeCustomer: boolean = false
): Promise<any> {
  let include = "";

  if (includeProfileImage)
    include += (include ? "," : "") + "profileImage";

  if (includeStripeAccount)
    include += (include ? "," : "") + "stripeAccount";

  if (includeStripeCustomer)
    include += (include ? "," : "") + "stripeCustomer.defaultPaymentMethod";

  const url = `${this.baseUrl}/api/current_user/show?include=${include}`;

  return this.withAuthRetry(async () => {
    return await this.apiService.get<any>(url, true, "main");
  });
}

async sharedTribecurrentUserShow(
  includeProfileImage: boolean = false,
  includeStripeAccount: boolean = false,
  includeStripeCustomer: boolean = false
): Promise<any> {
  const includeList: string[] = [];

  if (includeProfileImage) includeList.push("profileImage");
  if (includeStripeAccount) includeList.push("stripeAccount");
  if (includeStripeCustomer)
    includeList.push("stripeCustomer.defaultPaymentMethod");

  const includeParam =
    includeList.length > 0 ? `?include=${includeList.join(",")}` : "";

  const url = `${this.baseUrl}/api/current_user/show${includeParam}`;

  return this.withAuthRetry(async () => {

    const token = localStorage.getItem("flex_token");

    // Build headers conditionally
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      // No access token found, calling without Authorization header
    }

    const result = await fetch(url, {
      method: "GET",
      headers
    });

    const text = await result.text();
    if (!text.trim()) return { status: result.status };

    const json = JSON.parse(text);
    json.status = result.status;
    return json;
  });
}

async changePassword(
  currentPassword: string,
  newPassword: string
): Promise<any> {
  try {
    const url = `${this.baseUrl}/api/current_user/change_password?expand=true`;

    const payload = {
      currentPassword,
      newPassword
    };

    const response = await this.apiService.post<any>(
      url,
      payload,
      true,      // authenticated
      "main"     // triggers token refresh if 401
    );

    if (!response) {
      console.warn("Sharetribe: changePassword returned null/undefined");
      return null;
    }

    return response;
  } catch (err) {
    console.error("Sharetribe.changePassword error:", err);
    return null;
  }
}

async uploadImage(imageData: any, imageType: string): Promise<any> {
  const url = `${this.baseUrl}/api/images/upload?expand=true`;

  let blob: Blob;

  if (imageData instanceof Blob || imageData instanceof File) {
    blob = imageData;
  } else {
    const uint = imageData instanceof Uint8Array ? imageData : new Uint8Array(imageData);
    const buffer = uint.buffer.slice(uint.byteOffset, uint.byteOffset + uint.byteLength);
    blob = new Blob([buffer], { type: imageType });
  }

  const formData = new FormData();
  formData.append("image", blob, `image.${imageType.replace("image/", "")}`);

  return this.withAuthRetry(async () => {
    const token = localStorage.getItem("flex_token");

    const raw = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`
      },
      body: formData
    });

    if (raw.status === 401) {
      return { status: 401 };
    }

    const text = await raw.text();
    if (!text.trim()) {
      console.warn("EMPTY RESPONSE FROM SHARETRIBE", raw.status);
      return { status: raw.status }; // triggers retry if needed
    }

    let json;
    try {
      json = JSON.parse(text);
    } catch (err) {
      console.error("INVALID JSON FROM SHARETRIBE", text);
      return { status: raw.status };
    }

    json.status = raw.status; // for withAuthRetry to detect
    return json;
  });
}

async ownListingsCreate(
  title: string,
  description: string,
  geoLat: number,
  geoLng: number,
  priceCurrency: string | null,
  priceAmount: number,
  images: string[],
  availabilityPlan: Record<string, any> | null,
  privateData: Record<string, any> | null,
  publicData: Record<string, any> | null,
  includes?: string[]
): Promise<any> {

  const url = `${this.baseUrl}/api/own_listings/create`;

  const queryParams: Record<string, string> = { expand: "true" };
  if (includes?.length) queryParams.include = includes.join(",");

  const payload: any = { title };

  if (description) payload.description = description;

  if (geoLat !== 0 && geoLng !== 0) {
    payload.geolocation = { lat: geoLat, lng: geoLng };
  }

  payload.price = {
    currency: (priceCurrency || "USD").toUpperCase(),
    amount: priceAmount,
  };

  if (images?.length) payload.images = images;
  if (availabilityPlan) payload.availabilityPlan = availabilityPlan;
  if (publicData) payload.publicData = publicData;
  if (privateData) payload.privateData = privateData;

  const query = new URLSearchParams(queryParams).toString();
  const fullUrl = `${url}?${query}`;

 return this.withAuthRetry(async () => {
  const token = localStorage.getItem("flex_token");

  const response = await fetch(fullUrl, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }

  return response.json();
});

}

async ownListingsQuery(
  parameters: Record<string, string> = {},
  includes?: string[]
): Promise<{ data: any[]; included: any[] }> {

  const base = `${this.baseUrl}/api/own_listings/query`;

  const queryParams: Record<string, string> = {};

  for (const key in parameters) {
    queryParams[key] = parameters[key];
  }

  if (includes?.length) {
    queryParams["include"] = includes.join(",");
  }

  const queryString = new URLSearchParams(queryParams).toString();
  const fullUrl = queryString ? `${base}?${queryString}` : base;

  const token = localStorage.getItem("flex_token");

  const doRequest = async (accessToken: string) => {
    const resp = await fetch(fullUrl, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${accessToken}`,
        "Accept": "application/json"
      }
    });

    if (resp.status === 401) return null;

    const json = await resp.json();

    return json; // THIS returns { data: [...], included: [...], meta: ... }
  };

  let result = await doRequest(token!);

  if (!result) {
    const newToken = await this.retryWithRefreshToken();
    if (!newToken) throw new Error("Sharetribe token refresh failed.");

    localStorage.setItem("flex_token", newToken);
    result = await doRequest(newToken);
    if (!result) throw new Error("Sharetribe request failed even after refresh.");
  }

  return {
    data: result.data || [],
    included: result.included || []
  };
}

async ownListingsOpen(listingId: string): Promise<any> {
  const url = `${this.baseUrl}/api/own_listings/open?expand=true`;

  const payload = {
    id: listingId
  };

  return this.withAuthRetry(async () => {
    const token = localStorage.getItem("flex_token");

    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (resp.status === 401) {
      return { status: 401 };
    }

    return await resp.json();
  });
}

async ownListingsClose(listingId: string): Promise<any> {
  const url = `${this.baseUrl}/api/own_listings/close?expand=true`;

  const payload = {
    id: listingId
  };

  return this.withAuthRetry(async () => {
    const token = localStorage.getItem("flex_token");

    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (resp.status === 401) {
      return { status: 401 };
    }

    return await resp.json();
  });
}

async ownListingsCreateDraft(
  title: string,
  description: string,
  geoLat: number,
  geoLng: number,
  priceCurrency: string,
  priceAmount: number,
  images: string[],
  availabilityPlan: Record<string, any>,
  privateData: Record<string, any>,
  publicData: Record<string, any>,
  includes?: string[]
): Promise<any> {
  const url = `${this.baseUrl}/api/own_listings/create_draft`;

  // Build query params
  const queryParams: Record<string, string> = { expand: "true" };
  if (includes && includes.length > 0) {
    queryParams["include"] = includes.join(",");
  }

  // Build payload
  const payload: any = { title };

  if (description) payload.description = description;

  if (geoLat && geoLng) {
    payload.geolocation = { lat: geoLat, lng: geoLng };
  }

  payload.price = {
    currency: (priceCurrency || "USD").toUpperCase(),
    amount: priceAmount,
  };

  if (images?.length) payload.images = images;
  if (availabilityPlan) payload.availabilityPlan = availabilityPlan;
  if (privateData) payload.privateData = privateData;
  if (publicData) payload.publicData = publicData;

  // Final URL
  const query = new URLSearchParams(queryParams).toString();
  const fullUrl = `${url}?${query}`;

  // Execute with automatic refresh
  return this.withAuthRetry(() =>
    this.apiService.post<any>(fullUrl, payload, true, "main")
  );
}

async logout(): Promise<number> {
  try {
    const url = `${this.baseUrl}/auth/revoke`;

    // Refresh token loaded from localStorage
    const refreshToken = localStorage.getItem("flex_refresh_token");

    if (!refreshToken) {
      console.warn("No refresh token found for Sharetribe logout.");
      return 1;
    }

    const body = new URLSearchParams({
      token: refreshToken
    });

    // 1) Try revoke request
    let response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body
    });

    // If unauthorized, try refresh flow (if you support it)
    if (response.status === 401) {
      const newToken = await this.retryWithRefreshToken?.(); // Optional — based on your setup
      if (newToken) {
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body
        });
      }
    }

    // parse JSON
    const json = await response.json();

    if (json?.revoked === true) {
      // Clear access + refresh tokens
      localStorage.removeItem("flex_access_token");
      localStorage.removeItem("flex_refresh_token");
      return 0; // success
    }

    return 1; // revoke failed but no exception

  } catch (err) {
    console.error("Sharetribe.logout error:", err);
    return 2; // exception
  }
}

async retryWithRefreshToken(): Promise<string | null> {
  const url = `${this.baseUrl}/auth/token`;

  const refreshToken = localStorage.getItem("flex_refresh_token");
  if (!refreshToken) {
    return null;
  }

  const body = new URLSearchParams({
    client_id: this.shareTribe_client_id,
    grant_type: "refresh_token",
    scope: "user",
    refresh_token: refreshToken
  });

  try {

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body
    });

    if (!response.ok) {
      console.warn("Sharetribe.RetryWithRefreshToken:", await response.text());
      return null;
    }

    const json = await response.json();

    if (json.access_token) {
      localStorage.setItem("flex_token", json.access_token);
    }

    if (json.refresh_token) {
      localStorage.setItem("flex_refresh_token", json.refresh_token);
    }

    return json.access_token ?? null;

  } catch (ex) {
    console.log("Sharetribe.RetryWithRefreshToken", ex);
    return null;
  }
}

async deleteCurrentUser(currentPassword: string): Promise<any> {
  try {
    const url = `${this.baseUrl}/api/current_user/delete?expand=true`;

    const payload = { currentPassword };

    return await this.withAuthRetry(async () => {
      return await this.apiService.post<any>(url, payload, true, "main");
    });
  } catch (err) {
    console.error("Sharetribe.deleteCurrentUser failed:", err);
    return null;
  }
}

private async withAuthRetry<T>(action: () => Promise<T>): Promise<T> {
  let response: any = await action();

  const isExpired =
    response &&
    (response.status === 401 ||
     response.status === 403 ||
     response.status_code === 401 ||
     response?.meta?.status === 401);

  if (isExpired) {
    // Token expired, refreshing token

    const newToken = await this.retryWithRefreshToken();

    if (newToken) {
      // Token refreshed, retrying original request
      response = await action();
    } else {
      // Token refresh failed, user must re-login
    }
  }

  return response;
}

async ownListingsUpdate(
  listingId: string,
  title: string | null,
  description: string,
  geoLat: number,
  geoLng: number,
  priceCurrency: string | null,
  priceAmount: number,
  images: string[],
  availabilityPlan: Record<string, any> | null,
  privateData: Record<string, any> | null,
  publicData: Record<string, any> | null,
  includes?: string[]
): Promise<any> {

  const url = `${this.baseUrl}/api/own_listings/update`;

  const queryParams: Record<string, string> = { expand: "true" };
  if (includes?.length) queryParams.include = includes.join(",");

  const payload: any = { id: listingId };

  if (title !== null) payload.title = title;
  if (description) payload.description = description;

  if (geoLat !== 0 && geoLng !== 0) {
    payload.geolocation = { lat: geoLat, lng: geoLng };
  }

  if (priceAmount > 0) {
    payload.price = {
      currency: (priceCurrency || "USD").toUpperCase(),
      amount: priceAmount,
    };
  }

  if (images?.length) payload.images = images;
  if (availabilityPlan) payload.availabilityPlan = availabilityPlan;
  if (privateData) payload.privateData = privateData;
  if (publicData) payload.publicData = publicData;

  const query = new URLSearchParams(queryParams).toString();
  const fullUrl = `${url}?${query}`;

  const token = localStorage.getItem("flex_token");

  return this.withAuthRetry(async () => {
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error);
    }

    return response.json();
  });
}

async listingsQuery(
  parameters: Record<string, string> = {},
  includes?: string[]
): Promise<{ data: any[]; included: any[]; meta: any }> {

  const base = `${this.baseUrl}/api/listings/query`;

  const queryParams: Record<string, string> = {};

  for (const key in parameters) {
    queryParams[key] = parameters[key];
  }

  if (includes?.length) {
    queryParams["include"] = includes.join(",");
  }

  const queryString = new URLSearchParams(queryParams).toString();
  const fullUrl = queryString ? `${base}?${queryString}` : base;

  let token = localStorage.getItem("flex_token");

  const doRequest = async (tk: string | null) => {
    const resp = await fetch(fullUrl, {
      method: "GET",
      headers: {
        "Authorization": tk ? `Bearer ${tk}` : "",
        "Accept": "application/json"
      }
    });

    if (resp.status === 401) return null;

    const json = await resp.json();
    return json;
  };

  let result = await doRequest(token);

  if (!result) {
    const newToken = await this.retryWithRefreshToken();
    if (newToken) {
      localStorage.setItem("flex_token", newToken);
      result = await doRequest(newToken);
    } else {
      const anonToken = await this.retryWithAnonToken();
      if (!anonToken) throw new Error("Failed to fetch listings");
      result = await doRequest(anonToken);
    }
  }

  // ALWAYS return {data, included, meta}
  return {
    data: result?.data || [],
    included: result?.included || [],
    meta: result?.meta || {}
  };
}

async retryWithAnonToken(): Promise<string | null> {
  try {
    const url = `${this.baseUrl}/auth/token`;

    const body = new URLSearchParams({
      client_id: this.shareTribe_client_id,  
      client_secret: "55cb45cf21276bb53e9123ef7681c136ad44dc07",                
      grant_type: "client_credentials",
      scope: "public-read"
    });

    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body
    });

    if (!resp.ok) {
      console.error("retryWithAnonToken failed:", await resp.text());
      return null;
    }

    const json = await resp.json();

    if (!json.access_token) return null;

    // Store like C# tokenStore.SetToken()
    localStorage.setItem("flex_token", json.access_token);

    return json.access_token;
  } catch (err) {
    console.error("retryWithAnonToken error:", err);
    return null;
  }
}


async reviewsQuery(
  transactionId?: string | null,
  listingId?: string | null,
  subjectId?: string | null,
  type?: string | null,
  state?: string | null,
  includes?: string[]
): Promise<any> {
  try {
    const url = `${this.baseUrl}/api/reviews/query`;

    // Build query params exactly like C#
    const query: Record<string, string> = {};

    if (transactionId) query.transactionId = transactionId;
    if (listingId) query.listingId = listingId;
    if (subjectId) query.subjectId = subjectId;
    if (type) query.type = type;
    if (state) query.state = state;

    if (includes?.length) {
      query.include = includes.join(",");
    }

    const queryString = new URLSearchParams(query).toString();
    const fullUrl = `${url}?${queryString}`;

    return this.withAuthRetry(async () => {
      const token = localStorage.getItem("flex_token");

      const resp = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (!resp.ok) {
        console.error("reviewsQuery error:", await resp.text());
        return { data: [], included: [] };
      }

      return resp.json();
    });
  } catch (err) {
    console.error("Sharetribe.reviewsQuery failed:", err);
    return { data: [], included: [] };
  }
}


async findSwimmers(
  userId: number,
  coachUserId: number,
  findName: string,
  page: number
): Promise<any[]> {

  const url = `${this.config.baseUrl}/user/find_swimmers`;

  try {
    const body = new URLSearchParams();
    body.append("user_id", userId.toString());
    body.append("coach_id", coachUserId.toString());
    body.append("name", findName);
    body.append("page", page.toString());


     const token = localStorage.getItem('auth_token');
    // POST using apiService
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        'Authorization': `Bearer ${token}`
      },
      body
    });

    const text = await response.text();
    if (!text) return [];

    const json = JSON.parse(text);

    // json = { status, msg, data: "JSON STRING LIST" }
    const parsed = JSON.parse(json.data);

    return parsed || [];
  } catch (err) {
    console.error("findSwimmers API error:", err);
    return [];
  }
}

async swimmerConnectToSwimmer(
  userId: number,
  friendId: number
): Promise<any> {

  const url = `${this.config.baseUrl}/user/swimmer_connect_to_swimmer`;

  try {
    const body = new URLSearchParams();
    body.append("user_id", userId.toString());
    body.append("friend_id", friendId.toString());

    const token = localStorage.getItem("auth_token");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Authorization": `Bearer ${token}`
      },
      body
    });

    const text = await response.text();
    if (!text) return { status: 0, msg: "No response" };

    const json = JSON.parse(text);
    return json;   // 🔥 FULL RESPONSE

  } catch (err) {
    console.error("SwimmerConnectToSwimmer error:", err);
    return { status: 0, msg: "Network error" };
  }
}

async coachConnectToSwimmer(
  coachId: number,
  userId: number
): Promise<any> {

  const url = `${this.config.baseUrl}/coaches/coach_connect_to_swimmer`;

  try {
    const body = new URLSearchParams();
    body.append("coach_id", coachId.toString());
    body.append("user_id", userId.toString());

    const token = localStorage.getItem("auth_token");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Authorization": `Bearer ${token}`
      },
      body
    });

    const text = await response.text();
    if (!text) return { status: 0, msg: "No response" };

    const json = JSON.parse(text);
    return json;  

  } catch (err) {
    console.error("CoachConnectToSwimmer error:", err);
    return { status: 0, msg: "Network error" };
  }
}





async getUserById(userId: number): Promise<any | null> {
  const url = `${this.config.baseUrl}/user/get_user/id/${userId}`;

  try {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
         "Authorization": `Bearer ${token}`
      }
    });

    const text = await response.text();
    if (!text) return null;

    const json = JSON.parse(text);

    if (json.status === 1 && json.data) {
      return json.data;
    }

    return null;

  } catch (err) {
    console.error("getUserById error:", err);
    return null;
  }
}

async getSwimmerCoaches(
  swimmerId: number,
  status: number
): Promise<any[]> {

  const url = `${this.config.baseUrl}/coaches/get_swimmer_coaches`;

  try {
    const body = new URLSearchParams();
    body.append("swimmer_id", swimmerId.toString());
    body.append("status", status.toString());

    const token = localStorage.getItem("auth_token");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Authorization": `Bearer ${token}`,
      },
      body
    });

    const text = await response.text();
    if (!text) return [];

    const json = JSON.parse(text);

    if (!json.data) return [];

    // C# API returns array serialized as string
    const parsed = JSON.parse(json.data);

    return parsed || [];

  } catch (err) {
    // Error fetching swimmer coaches
    // In production, consider logging to error tracking service
    return [];
  }
}

async getCoachSwimmers(
  coachId: number,
  status: number
): Promise<any[]> {

  const url = `${this.config.baseUrl}/coaches/get_coach_swimmers`;

  try {
    const body = new URLSearchParams();
    body.append("coach_id", coachId.toString());
    body.append("status", status.toString());

    const token = localStorage.getItem("auth_token");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Authorization": `Bearer ${token}`,
      },
      body
    });

    const text = await response.text();
    if (!text) return [];
    const json = JSON.parse(text);

    if (!json.data) return [];

    const parsed = JSON.parse(json.data);

    return parsed || [];

  } catch (err) {
    // Error fetching coach swimmers
    // In production, consider logging to error tracking service
    return [];
  }
}

  private mapToDomain(dto: SharetribeResponse): Sharetribe {
    return Sharetribe.fromData({
      id: dto.id,
      user_id: dto.user_id,
      title: dto.title,
      description: dto.description,
      category: dto.category,
      price: dto.price,
      currency: dto.currency,
      images: dto.images,
      location: dto.location,
      latitude: dto.latitude,
      longitude: dto.longitude,
      is_active: dto.is_active,
      is_published: dto.is_published,
      views: dto.views,
      likes: dto.likes,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      published_at: dto.published_at
    });
  }

  private mapToDto(domain: Sharetribe): CreateSharetribeRequest | UpdateSharetribeRequest {
  return {
    id: domain.id,
    userId: domain.userId,
    title: domain.title,
    description: domain.description,
    category: domain.category,
    price: {
      amount: domain.price,
      currency: domain.currency
    },
    images: domain.images,
    location: domain.location,
    latitude: domain.latitude,
    longitude: domain.longitude,
    isActive: domain.isActive,
    isPublished: domain.isPublished,
    views: domain.views,
    likes: domain.likes,
    createdAt: domain.createdAt.toISOString(),
    updatedAt: domain.updatedAt.toISOString(),
    publishedAt: domain.publishedAt?.toISOString()
  };
}

  async findById(id: string): Promise<Sharetribe | null> {
  try {
    const response = await this.apiService.get<SharetribeResponse>(`/sharetribe/${id}`);
    if (!response) {
      return null;
    }
    return this.mapToDomain(response);
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to find Sharetribe by ID: ${id}`, {
      operation: 'SharetribeRepository.findById',
      id,
      error,
    });
  }
}


  async findByUserId(userId: string): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>(`/sharetribe/user/${userId}`);
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to find sharetribes by user ID: ${userId}`, {
      operation: 'SharetribeRepository.findByUserId',
      userId,
      error,
    });
  }
}

  async findPublished(): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>('/sharetribe/published');
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to find published listings', {
      operation: 'SharetribeRepository.findPublished',
      error,
    });
  }
}

async save(sharetribe: Sharetribe): Promise<Sharetribe> {
  try {
    const dto = { ...this.mapToDto(sharetribe) };
    const response = await this.apiService.post<SharetribeResponse>('/sharetribe', dto);
    return this.mapToDomain(response);
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to save sharetribe: ${sharetribe.id}`, {
      operation: 'SharetribeRepository.save',
      sharetribeId: sharetribe.id,
      error,
    });
  }
}

async update(sharetribe: Sharetribe): Promise<Sharetribe> {
  try {
    const dto = { ...this.mapToDto(sharetribe) }; 
    const response = await this.apiService.put<SharetribeResponse>(`/sharetribe/${sharetribe.id}`, dto);
    return this.mapToDomain(response);
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to update sharetribe: ${sharetribe.id}`, {
      operation: 'SharetribeRepository.update',
      sharetribeId: sharetribe.id,
      error,
    });
  }
}



 async delete(id: string): Promise<void> {
  try {
    await this.apiService.delete(`/sharetribe/${id}`);
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to delete sharetribe: ${id}`, {
      operation: 'SharetribeRepository.delete',
      id,
      error,
    });
  }
}

async publish(sharetribeId: string): Promise<void> {
  try {
    await this.apiService.post(`/sharetribe/${sharetribeId}/publish`, {});
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to publish sharetribe: ${sharetribeId}`, {
      operation: 'SharetribeRepository.publish',
      sharetribeId,
      error,
    });
  }
}

async unpublish(sharetribeId: string): Promise<void> {
  try {
    await this.apiService.post(`/sharetribe/${sharetribeId}/unpublish`, {});
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to unpublish sharetribe: ${sharetribeId}`, {
      operation: 'SharetribeRepository.unpublish',
      sharetribeId,
      error,
    });
  }
}

async view(sharetribeId: string): Promise<void> {
  try {
    await this.apiService.post(`/sharetribe/${sharetribeId}/view`, {});
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to view sharetribe: ${sharetribeId}`, {
      operation: 'SharetribeRepository.view',
      sharetribeId,
      error,
    });
  }
}

async like(sharetribeId: string, userId: string): Promise<void> {
  try {
    await this.apiService.post(`/sharetribe/${sharetribeId}/like`, { user_id: userId });
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to like sharetribe: ${sharetribeId}`, {
      operation: 'SharetribeRepository.like',
      sharetribeId,
      userId,
      error,
    });
  }
}

async unlike(sharetribeId: string, userId: string): Promise<void> {
  try {
    await this.apiService.post(`/sharetribe/${sharetribeId}/unlike`, { user_id: userId });
  } catch (error: unknown) {
    throw new InternalServerError(`Failed to unlike sharetribe: ${sharetribeId}`, {
      operation: 'SharetribeRepository.unlike',
      sharetribeId,
      userId,
      error,
    });
  }
}

  async search(query: string): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>(
      `/sharetribe/search?query=${encodeURIComponent(query)}`
    );
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to search sharetribe listings', {
      operation: 'SharetribeRepository.search',
      query,
      error,
    });
  }
}

async findByCategory(category: string): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>(
      `/sharetribe/category/${category}`
    );
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to find by category', {
      operation: 'SharetribeRepository.findByCategory',
      category,
      error,
    });
  }
}

async findByLocation(location: string, radius?: number): Promise<Sharetribe[]> {
  try {
    const params = new URLSearchParams({ location });
    if (radius) params.append('radius', radius.toString());
    const response = await this.apiService.get<SharetribeResponse[]>(
      `/sharetribe/location?${params}`
    );
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to find by location', {
      operation: 'SharetribeRepository.findByLocation',
      location,
      radius,
      error,
    });
  }
}

async findByPriceRange(minPrice: number, maxPrice: number): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>(
      `/sharetribe/price-range?min=${minPrice}&max=${maxPrice}`
    );
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to find by price range', {
      operation: 'SharetribeRepository.findByPriceRange',
      minPrice,
      maxPrice,
      error,
    });
  }
}

async findTrending(limit: number): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>(
      `/sharetribe/trending?limit=${limit}`
    );
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to find trending listings', {
      operation: 'SharetribeRepository.findTrending',
      limit,
      error,
    });
  }
}

async findFeatured(): Promise<Sharetribe[]> {
  try {
    const response = await this.apiService.get<SharetribeResponse[]>('/sharetribe/featured');
    return response.map(this.mapToDomain);
  } catch (error: unknown) {
    throw new InternalServerError('Failed to find featured listings', {
      operation: 'SharetribeRepository.findFeatured',
      error,
    });
  }
}

async getSharetribeStats(sharetribeId: string): Promise<{
  views: number;
  likes: number;
  engagementRate: number;
}> {
  try {
    const response = await this.apiService.get<{
      views: number;
      likes: number;
      engagement_rate: number;
    }>(`/sharetribe/${sharetribeId}/stats`);

    return {
      views: response.views,
      likes: response.likes,
      engagementRate: response.engagement_rate,
    };
  } catch (error: unknown) {
    throw new InternalServerError('Failed to get sharetribe stats', {
      operation: 'SharetribeRepository.getSharetribeStats',
      sharetribeId,
      error,
    });
  }
}

async findPaginated(userId: string, page: number, limit: number): Promise<{
  sharetribes: Sharetribe[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}> {
  try {
    const response = await this.apiService.get<{
      sharetribes: SharetribeResponse[];
      total: number;
      page: number;
      limit: number;
      total_pages: number;
    }>(`/sharetribe/user/${userId}/paginated?page=${page}&limit=${limit}`);

    return {
      sharetribes: response.sharetribes.map(this.mapToDomain),
      total: response.total,
      page: response.page,
      limit: response.limit,
      totalPages: response.total_pages,
    };
  } catch (error: unknown) {
    throw new InternalServerError('Failed to get paginated sharetribes', {
      operation: 'SharetribeRepository.findPaginated',
      userId,
      page,
      limit,
      error,
    });
  }
}

}
